import { cn } from "@/utils";
import { isEscapeOwnedByOverlay } from "@/utils/overlayOwnsEscape";
import type { PostMessageParams, ReferencedEntity } from "@cohesive/api-client";
import { MessageSquare, X } from "lucide-react";
import { motion } from "motion/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { QuickChatSubmitResult } from "../hooks/useQuickChat";
import {
  CanvasComposerInput,
  type CanvasComposerInputResult,
} from "./CanvasComposerInput";

const COMPOSER_WIDTH = 420;

interface QuickChatComposerProps {
  position: { x: number; y: number };
  canvasId: string;
  isSubmitting?: boolean;
  /** Pre-filled text (e.g. from voice transcription) */
  initialContent?: string;
  onSubmit: (
    content: string,
    referencedEntities: ReferencedEntity[],
    composerPos: { x: number; y: number },
    agentReasoningEffort?: string,
    harnessReasoningEffort?: string,
  ) => Promise<QuickChatSubmitResult>;
  onCancel: () => void;
}

function clampPos(x: number, y: number) {
  const APPROX_H = 240;
  return {
    x: Math.max(8, Math.min(x, window.innerWidth - COMPOSER_WIDTH - 8)),
    y: Math.max(
      8,
      Math.min(y - APPROX_H / 2, window.innerHeight - APPROX_H - 8),
    ),
  };
}

export function QuickChatComposer({
  position,
  canvasId,
  isSubmitting = false,
  initialContent,
  onSubmit,
  onCancel,
}: QuickChatComposerProps) {
  const [pos, setPos] = useState(() => clampPos(position.x, position.y));
  const dragState = useRef<{
    dragging: boolean;
    startX: number;
    startY: number;
    initX: number;
    initY: number;
  }>({ dragging: false, startX: 0, startY: 0, initX: 0, initY: 0 });

  // Inject the live composer position into the canvas submit handler so the
  // chat-session node lands where the (possibly dragged) composer sits.
  const handleSubmit = useCallback(
    (
      content: string,
      referencedEntities: ReferencedEntity[],
      options: Pick<
        PostMessageParams,
        | "reasoning_effort"
        | "model"
        | "harness_reasoning_effort"
        | "permission_mode"
      >,
    ): Promise<CanvasComposerInputResult> =>
      // model / permission_mode are dropped on purpose: this composer always mints its own
      // session, which carries the harness choice on its create and the default mode.
      onSubmit(
        content,
        referencedEntities,
        pos,
        options.reasoning_effort,
        options.harness_reasoning_effort,
      ),
    [onSubmit, pos],
  );

  const handleDragStart = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return;
      const target = e.target as HTMLElement;
      if (target.closest("button")) return;

      e.preventDefault();
      dragState.current = {
        dragging: true,
        startX: e.clientX,
        startY: e.clientY,
        initX: pos.x,
        initY: pos.y,
      };

      const handleMove = (ev: MouseEvent) => {
        if (!dragState.current.dragging) return;
        const dx = ev.clientX - dragState.current.startX;
        const dy = ev.clientY - dragState.current.startY;
        const APPROX_H = 240;
        setPos({
          x: Math.max(
            8,
            Math.min(
              dragState.current.initX + dx,
              window.innerWidth - COMPOSER_WIDTH - 8,
            ),
          ),
          y: Math.max(
            8,
            Math.min(
              dragState.current.initY + dy,
              window.innerHeight - APPROX_H - 8,
            ),
          ),
        });
      };

      const handleUp = () => {
        dragState.current.dragging = false;
        document.removeEventListener("mousemove", handleMove);
        document.removeEventListener("mouseup", handleUp);
      };

      document.addEventListener("mousemove", handleMove);
      document.addEventListener("mouseup", handleUp);
    },
    [pos],
  );

  // Escape unwinds one layer at a time. While a selector opened from the
  // composer is up (skills, add content, thinking effort), that menu takes the
  // keypress and the composer — with the message still in it — stays put; the
  // next Escape cancels the composer.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (isEscapeOwnedByOverlay(e)) return;
      onCancel();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onCancel]);

  return createPortal(
    <motion.div
      initial={{ opacity: 0, scale: 0.92 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.92 }}
      transition={{ duration: 0.15, ease: "easeOut" }}
      className="fixed z-[10000] quick-chat-composer"
      style={{ left: pos.x, top: pos.y, width: COMPOSER_WIDTH }}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
      onWheel={(e) => e.stopPropagation()}
    >
      <div
        className={cn(
          // Design-system panel chrome: rounded-xl + hairline edge + elev-3.
          "bg-surface-floating-chat/95 backdrop-blur-xl border border-n-10 rounded-xl shadow-elev-3",
          "flex flex-col overflow-hidden",
        )}
      >
        <div
          className="flex items-center justify-between px-4 h-12 border-b border-n-10 cursor-grab active:cursor-grabbing select-none shrink-0"
          onMouseDown={handleDragStart}
        >
          <div className="flex items-center gap-2">
            <MessageSquare className="size-[18px] text-rust" strokeWidth={2} />
            <span className="font-sans text-sm font-medium text-n-90">
              Quick Chat
            </span>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="h-7 w-7 flex items-center justify-center rounded-sm text-n-80 outline-none hover:bg-n-10 transition-colors focus-visible:outline-2 focus-visible:outline-rust"
          >
            <X className="size-3.5" />
          </button>
        </div>

        <CanvasComposerInput
          canvasId={canvasId}
          initialContent={initialContent}
          isSubmitting={isSubmitting}
          onSubmit={handleSubmit}
          autoFocus
          className="shrink-0 px-2 pb-2 pt-2"
        />
      </div>
    </motion.div>,
    document.body,
  );
}
